// Consent (P0.11) — FR-44's off switch, and the thing that decides whether an SDK is downloaded
// at all.
//
// The design decision worth stating: **opting out means the transport is never fetched.** Not
// loaded-and-silent — never fetched, never parsed, never executed. No timers, no listeners, no
// fingerprinting surface, and 101 kB of SDK that never touches the wire.
//
// FR-44 only asks for an "off switch in settings". Loading PostHog and telling it not to send
// would satisfy the sentence. It would not satisfy G4 — "Trustworthy by design; privacy posture
// is a stated feature" — because a user who opted out and then watched devtools would see an
// analytics SDK download anyway, and be right to distrust the claim. The strongest version costs
// nothing here, so it is the one to build.
//
// Default: **opted in.** That is a real decision and it deserves the discomfort of being written
// down. FR-44 permits it (an off switch, not an on switch); the events are anonymous by
// construction and PostHog is EU-hosted per Stack §9. But §9's own success metrics are the
// argument — a product whose activation rate is unmeasurable cannot be improved for the users it
// is failing. **This is the line to revisit if OQ-S1 is ever reopened**, and the one a privacy
// reviewer will go to first.

const STORAGE_KEY = 'synapse.telemetry.optOut';

/**
 * Has the user opted out?
 *
 * Reads storage every call rather than caching. It is a localStorage hit on a path that runs
 * once per event, not per frame — and the alternative is a stale cache deciding to send data
 * after someone has just asked it not to. That trade only has one answer.
 */
export function hasOptedOut(): boolean {
  try {
    return globalThis.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    // Storage can throw: Safari private mode, disabled cookies, a locked-down profile. Treat an
    // unreadable preference as opted out. If we cannot tell what the user wanted, not sending is
    // the answer that cannot be wrong in a way that matters.
    return true;
  }
}

export function setOptedOut(optedOut: boolean): void {
  try {
    globalThis.localStorage.setItem(STORAGE_KEY, String(optedOut));
  } catch {
    // Nothing to do, and nothing to report: the read path already fails closed.
  }
}

/** True when telemetry may be loaded and sent. */
export function hasConsent(): boolean {
  return !hasOptedOut();
}
